import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import MultiEmail from "./MultiEmail";
import CompagniaSmartField from "./CompagniaSmartField";
import WhatsAppLink from "./WhatsAppLink";
import { IconTel, IconEmail, IconPec, IconAddr, IconNote } from "./RubricaIcons";

const EMPTY = { nome: "", cognome: "", azienda: "", categoria_id: "", telefono: "", telefono_extra: [], email: "", email_extra: [], pec: "", pec_extra: [],
  indirizzo: "", numero_civico: "", cap: "", comune: "", provincia: "", note: "" };
const LBL = "text-slate-300 text-xs uppercase tracking-wide inline-flex items-center gap-1.5";
const INP = "mt-1.5 h-9 w-full px-3 rounded-md bg-slate-900/60 border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-amber-500/60";

// Scheda contatto della rubrica dello studio (nuovo o modifica).
export default function RubricaContattoDialog({ open, onOpenChange, contatto, categorie, onSaved, onMail, onPec }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...EMPTY, ...(contatto || {}) }); }, [open, contatto]);
  const txt = (k, label, cls = "") => (
    <div className={cls}><label className={LBL}>{label}</label>
      <input value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`rub-f-${k}`} className={INP} /></div>
  );
  const multi = (k, label, icon, opts) => (
    <div className="sm:col-span-2"><label className={LBL}>{icon}{label}</label>
      <MultiEmail value={form[k]} extra={form[`${k}_extra`]} testId={`rub-f-${k}`} {...opts}
        onChange={(main, ex) => setForm((f) => ({ ...f, [k]: main, [`${k}_extra`]: ex }))} /></div>
  );
  const save = async () => {
    setSaving(true);
    try {
      const { data } = contatto?.id ? await api.put(`/studio/rubrica-studio/contatti/${contatto.id}`, form) : await api.post("/studio/rubrica-studio/contatti", form);
      toast.success(contatto?.id ? "Contatto aggiornato" : "Contatto aggiunto");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0f1420] text-slate-100 border border-slate-800" data-testid="rub-contatto-dialog">
        <DialogHeader><DialogTitle>{contatto?.id ? "Modifica contatto" : "Nuovo contatto"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {txt("cognome", "Cognome")}
          {txt("nome", "Nome")}
          {txt("azienda", "Azienda / Ente", "sm:col-span-2")}
          <div className="sm:col-span-2"><label className={LBL}>Categoria</label>
            <select value={form.categoria_id || ""} onChange={(e) => setForm((f) => ({ ...f, categoria_id: e.target.value }))} data-testid="rub-f-categoria" className={INP}>
              <option value="">— Nessuna —</option>
              {categorie.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select></div>
          {multi("telefono", "Telefoni", <IconTel />, { type: "tel", star: false, label: "telefono", altLabel: "altro telefono",
            renderAfter: (v, i) => <WhatsAppLink number={v} size="h-5 w-5" testId={`rub-f-wa-${i}`} /> })}
          {multi("email", "Email", <IconEmail />, { label: "email",
            renderAfter: (v, i) => <button type="button" onClick={() => onMail && onMail(v)} title="Invia email" data-testid={`rub-f-send-email-${i}`} className="text-orange-500 hover:text-orange-400"><Send className="h-4 w-4" /></button> })}
          {multi("pec", "PEC", <IconPec />, { label: "PEC",
            renderAfter: (v, i) => <button type="button" onClick={() => onPec && onPec(v)} title="Invia PEC" data-testid={`rub-f-send-pec-${i}`} className="text-blue-600 hover:text-blue-500"><Send className="h-4 w-4" /></button> })}
          <div className="sm:col-span-2 grid grid-cols-[1fr_7rem] gap-3">
            <div><label className={LBL}><IconAddr />Indirizzo</label><CompagniaSmartField fkey="indirizzo" form={form} setForm={setForm} pool={{}} testId="rub-f-indirizzo" /></div>
            {txt("numero_civico", "N° Civico")}
          </div>
          <div><label className={LBL}>Comune</label><CompagniaSmartField fkey="comune" form={form} setForm={setForm} pool={{}} testId="rub-f-comune" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LBL}>Provincia</label><CompagniaSmartField fkey="provincia" form={form} setForm={setForm} pool={{}} testId="rub-f-provincia" /></div>
            <div><label className={LBL}>CAP</label><CompagniaSmartField fkey="cap" form={form} setForm={setForm} pool={{}} testId="rub-f-cap" /></div>
          </div>
          <div className="sm:col-span-2"><label className={LBL}><IconNote />Note</label>
            <textarea value={form.note || ""} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={3} data-testid="rub-f-note"
              className="mt-1.5 w-full resize-y px-3 py-2 rounded-md bg-slate-900/60 border border-slate-700 text-sm text-slate-100" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="rub-f-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="rub-f-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
